import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PiUsersThreeLight, PiEyeLight, PiBrowserLight, PiDownloadSimpleLight } from "react-icons/pi";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LabelList, Cell,
} from "recharts";
import client from "../../api/client";

function downloadCsv(filename, headers, rows) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportButton({ onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#294a93] border border-gray-200 hover:border-[#294a93]/30 rounded-lg px-2.5 py-1.5 transition-colors">
      <PiDownloadSimpleLight size={14} /> Export CSV
    </button>
  );
}

// Validated categorical palette (dataviz skill, references/palette.md) — fixed
// order, never reassigned per-series when data changes.
const BLUE   = "#2a78d6";
const ORANGE = "#eb6834";
const AQUA   = "#1baf7a";
const YELLOW = "#eda100";
const MAGENTA = "#e87ba4";
const GREEN  = "#008300";
const VIOLET = "#4a3aa7";
const RED    = "#e34948";
const CHANNEL_COLORS = [BLUE, ORANGE, AQUA, YELLOW, MAGENTA, GREEN, VIOLET, RED];

function StatCard({ icon: Icon, label, value, color, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      <div className="min-w-0">
        {loading
          ? <div className="h-8 w-20 bg-gray-100 rounded animate-pulse mb-1" />
          : <p className="text-3xl font-extrabold text-gray-900 leading-none tabular-nums">{(value ?? 0).toLocaleString()}</p>
        }
        <p className="text-xs font-semibold text-gray-500 mt-1 uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-gray-600">
          <span className="font-bold" style={{ color: p.color }}>{p.value.toLocaleString()}</span> {p.name}
        </p>
      ))}
    </div>
  );
};

// Horizontal bar card for a single-metric-by-category breakdown (device, browser, OS, …)
function CategoryBarCard({ title, subtitle, data, dataKey, nameKey, loading, exportFilename, exportHeaders, exportRow, nameFormatter }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-gray-900 text-base">{title}</h2>
          <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        {data.length > 0 && (
          <ExportButton onClick={() => downloadCsv(exportFilename, exportHeaders, data.map(exportRow))} />
        )}
      </div>
      {loading ? (
        <div className="h-52 bg-gray-50 rounded-xl animate-pulse" />
      ) : data.length === 0 ? (
        <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(150, data.length * 40)}>
          <BarChart data={data} barSize={20} layout="vertical" margin={{ top: 4, right: 40, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey={nameKey}
              width={90}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={nameFormatter}
            />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey={dataKey} name="Users" radius={[0, 6, 6, 0]}>
              {data.map((_, i) => (
                <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
              ))}
              <LabelList dataKey={dataKey} position="right" style={{ fontSize: 11, fill: "#374151", fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    setLoading(true);
    setError("");
    client.get("/admin/analytics", { params: { days } })
      .then((res) => {
        if (res.data?.success === false) {
          setError(res.data?.message || "Could not load analytics.");
          setData(null);
        } else {
          setData(res.data);
        }
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Could not load analytics.");
        toast.error("Failed to load analytics.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const overview = data?.overview ?? [];
  const topPages = data?.top_pages ?? [];
  const trafficSources = data?.traffic_sources ?? [];
  const locations = data?.locations ?? [];
  const devices = data?.devices ?? [];
  const cities = data?.cities ?? [];
  const browsers = data?.browsers ?? [];
  const operatingSystems = data?.operating_systems ?? [];

  const totals = overview.reduce(
    (acc, d) => ({
      active_users: acc.active_users + d.active_users,
      page_views:   acc.page_views + d.page_views,
      sessions:     acc.sessions + d.sessions,
    }),
    { active_users: 0, page_views: 0, sessions: 0 }
  );

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-400 mt-0.5">Traffic from Google Analytics</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {error ? (
        <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-8 text-center">
          <p className="text-3xl mb-3">📡</p>
          <p className="text-sm font-medium text-gray-700">{error}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={PiUsersThreeLight} label="Active Users" value={totals.active_users} color="bg-[#2a78d6]" loading={loading} />
            <StatCard icon={PiEyeLight} label="Page Views" value={totals.page_views} color="bg-[#eb6834]" loading={loading} />
            <StatCard icon={PiBrowserLight} label="Sessions" value={totals.sessions} color="bg-[#1baf7a]" loading={loading} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-gray-900 text-base">Traffic Over Time</h2>
                <p className="text-xs text-gray-400 mt-0.5">Active users, page views & sessions per day</p>
              </div>
              {overview.length > 0 && (
                <ExportButton onClick={() => downloadCsv(
                  `traffic-${days}d.csv`,
                  ["Date", "Active Users", "Page Views", "Sessions"],
                  overview.map((d) => [d.date, d.active_users, d.page_views, d.sessions])
                )} />
              )}
            </div>
            {loading ? (
              <div className="h-64 bg-gray-50 rounded-xl animate-pulse" />
            ) : overview.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={overview} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="active_users" name="Active Users" stroke={BLUE} strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="page_views" name="Page Views" stroke={ORANGE} strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="sessions" name="Sessions" stroke={AQUA} strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Acquisition</h2>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 -mt-4">

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-gray-900 text-base">Top Pages</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Most viewed pages this period</p>
                </div>
                {topPages.length > 0 && (
                  <ExportButton onClick={() => downloadCsv(
                    `top-pages-${days}d.csv`,
                    ["Page", "Views"],
                    topPages.map((p) => [p.page_path, p.page_views])
                  )} />
                )}
              </div>
              {loading ? (
                <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
              ) : topPages.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Page</th>
                      <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Views</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {topPages.map((p) => (
                      <tr key={p.page_path}>
                        <td className="py-2.5 text-gray-700 max-w-[280px] truncate" title={p.page_path}>{p.page_path}</td>
                        <td className="py-2.5 text-right font-semibold text-gray-800 tabular-nums">{p.page_views.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-gray-900 text-base">Traffic Sources</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Sessions by channel</p>
                </div>
                {trafficSources.length > 0 && (
                  <ExportButton onClick={() => downloadCsv(
                    `traffic-sources-${days}d.csv`,
                    ["Channel", "Sessions"],
                    trafficSources.map((t) => [t.channel, t.sessions])
                  )} />
                )}
              </div>
              {loading ? (
                <div className="h-52 bg-gray-50 rounded-xl animate-pulse" />
              ) : trafficSources.length === 0 ? (
                <div className="h-52 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(210, trafficSources.length * 40)}>
                  <BarChart data={trafficSources} barSize={22} layout="vertical" margin={{ top: 4, right: 40, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="channel"
                      width={120}
                      tick={{ fontSize: 11, fill: "#6b7280" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="sessions" name="Sessions" radius={[0, 6, 6, 0]}>
                      {trafficSources.map((_, i) => (
                        <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
                      ))}
                      <LabelList dataKey="sessions" position="right" style={{ fontSize: 11, fill: "#374151", fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Users</h2>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 -mt-4">

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-gray-900 text-base">Top Locations</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Active users by country</p>
                </div>
                {locations.length > 0 && (
                  <ExportButton onClick={() => downloadCsv(
                    `locations-${days}d.csv`,
                    ["Country", "Users"],
                    locations.map((l) => [l.country, l.users])
                  )} />
                )}
              </div>
              {loading ? (
                <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
              ) : locations.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Country</th>
                      <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Users</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {locations.map((l) => (
                      <tr key={l.country}>
                        <td className="py-2.5 text-gray-700">{l.country}</td>
                        <td className="py-2.5 text-right font-semibold text-gray-800 tabular-nums">{l.users.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-gray-900 text-base">Top Cities</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Active users by city</p>
                </div>
                {cities.length > 0 && (
                  <ExportButton onClick={() => downloadCsv(
                    `cities-${days}d.csv`,
                    ["City", "Users"],
                    cities.map((c) => [c.city, c.users])
                  )} />
                )}
              </div>
              {loading ? (
                <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 bg-gray-50 rounded-lg animate-pulse" />)}</div>
              ) : cities.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-gray-400 text-sm">No data for this period</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">City</th>
                      <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-2">Users</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {cities.map((c) => (
                      <tr key={c.city}>
                        <td className="py-2.5 text-gray-700">{c.city}</td>
                        <td className="py-2.5 text-right font-semibold text-gray-800 tabular-nums">{c.users.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Tech</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 -mt-4">
            <CategoryBarCard
              title="By Device" subtitle="Active users by device type"
              data={devices} dataKey="users" nameKey="device" loading={loading}
              nameFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
              exportFilename={`devices-${days}d.csv`}
              exportHeaders={["Device", "Users"]}
              exportRow={(d) => [d.device, d.users]}
            />
            <CategoryBarCard
              title="Browser" subtitle="Active users by browser"
              data={browsers} dataKey="users" nameKey="browser" loading={loading}
              exportFilename={`browsers-${days}d.csv`}
              exportHeaders={["Browser", "Users"]}
              exportRow={(b) => [b.browser, b.users]}
            />
            <CategoryBarCard
              title="Operating System" subtitle="Active users by OS"
              data={operatingSystems} dataKey="users" nameKey="os" loading={loading}
              exportFilename={`operating-systems-${days}d.csv`}
              exportHeaders={["Operating System", "Users"]}
              exportRow={(o) => [o.os, o.users]}
            />
          </div>
        </>
      )}
    </div>
  );
}
